/**
 * Google Analytics 4 (GA4) Tracking Utility
 * Centralized analytics event tracking for PearlAI
 */

/**
 * Track a custom event to Google Analytics 4
 * @param {string} eventName - Name of the event
 * @param {object} params - Event parameters
 */
export const trackEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    console.warn('gtag not available');
    return;
  }

  window.gtag('event', eventName, params);
  console.log(`[GA4] Event tracked: ${eventName}`, params);
};

/**
 * Landing Page Events
 */
export const trackCtaTryDemoClicked = (location = 'hero') => {
  trackEvent('cta_try_demo_clicked', {
    button_location: location, // 'hero', 'navbar', 'footer'
    page_path: window.location.pathname
  });
};

export const trackCtaBookCallClicked = (location = 'hero') => {
  trackEvent('cta_book_call_clicked', {
    button_location: location, // 'hero', 'cta_section'
    page_path: window.location.pathname
  });
};

/**
 * Scroll Depth Tracking
 * Tracks when users scroll to specific milestones
 */
export const trackScrollDepth = (percentage) => {
  trackEvent('scroll_depth', {
    scroll_percentage: percentage,
    page_path: window.location.pathname
  });
};

/**
 * Time on Page Tracking
 * Tracks when user leaves page (beforeunload)
 */
export const trackTimeOnPage = (timeInSeconds, pageName = 'landing') => {
  trackEvent('time_on_page', {
    page_name: pageName,
    time_seconds: timeInSeconds,
    time_minutes: Math.round(timeInSeconds / 60 * 10) / 10, // Round to 1 decimal
    page_path: window.location.pathname
  });
};

/**
 * Demo Page Events
 */
export const trackDemoPageViewed = (isRevisit = false) => {
  trackEvent('demo_page_viewed', {
    is_revisit: isRevisit,
    page_path: '/demo'
  });
};

export const trackGenerateNoteClick = () => {
  trackEvent('generate_note_click', {
    page_path: '/demo'
  });
};

export const trackAudioPlayed = (action = 'play') => {
  trackEvent('audio_played', {
    action: action, // 'play', 'replay', 'pause'
    page_path: '/demo'
  });
};

export const trackSoapViewed = (timeToView = null) => {
  trackEvent('soap_viewed', {
    time_to_view: timeToView, // Time from generate click to viewing result
    page_path: '/demo'
  });
};

export const trackTimeOnDemoPage = (timeInSeconds) => {
  trackEvent('time_on_demo_page', {
    time_seconds: timeInSeconds,
    time_minutes: Math.round(timeInSeconds / 60 * 10) / 10,
    page_path: '/demo'
  });
};

export const trackDemoRevisit = (visitCount) => {
  trackEvent('demo_revisit', {
    visit_count: visitCount,
    page_path: '/demo'
  });
};

export const trackClickBookCallFromDemo = () => {
  trackEvent('click_book_call_from_demo', {
    source: 'demo_page',
    page_path: '/demo'
  });
};

/**
 * Scroll Depth Observer
 * Returns cleanup function
 */
export const setupScrollDepthTracking = () => {
  const milestones = [25, 50, 75, 100];
  const tracked = new Set();

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = Math.round((scrollTop / docHeight) * 100);

    milestones.forEach(milestone => {
      if (scrollPercent >= milestone && !tracked.has(milestone)) {
        tracked.add(milestone);
        trackScrollDepth(milestone);
      }
    });
  };

  window.addEventListener('scroll', handleScroll);

  // Return cleanup function
  return () => window.removeEventListener('scroll', handleScroll);
};

/**
 * Time on Page Observer
 * Returns cleanup function
 */
export const setupTimeOnPageTracking = (pageName = 'landing') => {
  const startTime = Date.now();

  const handleBeforeUnload = () => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    trackTimeOnPage(timeSpent, pageName);
  };

  window.addEventListener('beforeunload', handleBeforeUnload);

  // Return cleanup function
  return () => window.removeEventListener('beforeunload', handleBeforeUnload);
};

/**
 * Demo Revisit Tracking (using localStorage)
 */
export const trackDemoVisit = () => {
  try {
    const storageKey = 'pearlai_demo_visits';
    const visits = JSON.parse(localStorage.getItem(storageKey) || '{"count": 0, "lastVisit": null}');

    const now = new Date().toISOString();
    const isRevisit = visits.count > 0;

    // Increment visit count
    visits.count += 1;
    visits.lastVisit = now;

    localStorage.setItem(storageKey, JSON.stringify(visits));

    // Track the visit
    trackDemoPageViewed(isRevisit);

    if (isRevisit) {
      trackDemoRevisit(visits.count);
    }

    return isRevisit;
  } catch (err) {
    console.error('Failed to track demo visit:', err);
    return false;
  }
};
